import {
    getEndDate,
    isNoTrial,
    type MemberListItem,
    type Membership,
    MembershipStageId,
    type MembershipStagePaid,
    MembershipStageTrial,
    type MembershipTierId,
    PREFER_NOT_TO_USE_TITLE,
    TrialId,
} from "@albertine/types";
import type { MembershipTier } from "@albertine/types/openapi/lmt";
import { Lib } from "albertine-shared-web";
import { differenceInCalendarDays, isBefore, parseISO, subDays } from "date-fns";
import type { Agent } from "../common/types/Agent";
import type { Conversation } from "../common/types/Conversation";
import type { Dictionary } from "../common/types/Dictionary";
import {
    type EngagementLevel,
    EngagementLevelList,
    EngagementLevelMap,
} from "../common/types/Engagement";
import type { MemberGroupKey } from "../common/types/FilterGroups";
import type { FirebaseTimestamp } from "../common/types/FirebaseTimestamp";
import { defaultGroup, type GroupId } from "../common/types/Group";
import type { SortSettings } from "../common/types/Sort";
import { unassignedAgent } from "../common/utils/agents.util";
import { excludeUndefined } from "../common/utils/array.util";
import {
    sortByNumberComparator,
    sortByStringComparator,
} from "../common/utils/sort.util";
import { formatDefaultDate } from "../common/utils/time.formatted.util";
import { showDayMonthYearComparison } from "../common/utils/time.util";
import { isTypeOf } from "../common/utils/zod.util";
import { takeIfAnyStringMatches, takeIfStringMatches } from "./filter.util";
import { memberNameBySurname } from "./name.util";
import { fromTimestampToDate } from "./timestamp.util";
export type MembershipTypeCount = Omit<MembershipTier, "tenant"> & { count: number };

export type MembershipStageCount = {
    id: MembershipStageId;
    name: string;
    count: number;
};
const MembershipStageCountIdMap: Record<MembershipStageId, string> = {
    trial: "Trial",
    paid: "Paid",
};

export type FilterByEngagementLevel = {
    id: EngagementLevel;
    name: string;
    count: number;
};

const noTierGroup: MembershipTypeCount = {
    id: "no-tier",
    name: "Not set",
    active: true,
    default: false,
    count: 0,
};

export function isTrialExtended(trial: MembershipStageTrial) {
    return !!(trial.extendedFor && trial.extendedFor !== 0);
}

export function isMembershipCancelled(membership: MembershipStagePaid) {
    return membership.cancellation != null;
}

type MemberWithName = {
    firstName: string;
    lastName: string;
    title?: string | null;
};
type MemberWithHomeLocation = {
    city?: string | null;
    country?: string | null;
};
type MemberWithDeviceLocation = {
    deviceLocationPlace?: { city: string; isoCountryCode: string } | null;
    deviceLocation?: {
        place?: { city: string; isoCountryCode: string } | null;
    } | null;
};
export function lastKnownPlace(member: MemberWithDeviceLocation): string | undefined {
    const place = member.deviceLocationPlace ?? member.deviceLocation?.place;
    return place ? `${place.city}, ${place.isoCountryCode}` : undefined;
}

export function homeLocation(member: MemberWithHomeLocation) {
    const { city, country } = member;
    const location = [
        city?.length ? city : undefined,
        country?.length ? country : undefined,
    ]
        .filter(excludeUndefined)
        .join(", ");
    return location || "No location set";
}

export function nameInitials(firstName: string, lastName: string) {
    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
}

export function fullName(member: MemberWithName) {
    const title =
        member.title && member.title !== PREFER_NOT_TO_USE_TITLE
            ? `${member.title} `
            : "";
    return `${title}${member.firstName} ${member.lastName}`;
}

export function nameWithoutTitle(member: MemberWithName) {
    return `${member.firstName} ${member.lastName}`;
}

export function isTrialActive(membership: MembershipStageTrial, now: Date) {
    if (!membership.endTrialDate) return true;
    const endDate = new Date(membership.endTrialDate);
    return isBefore(now, endDate);
}

export function isMembershipActive(membership: MembershipStagePaid, now: Date) {
    if (!membership.endDate) return false;
    const endDate = new Date(membership.endDate);
    return isBefore(now, endDate);
}

const archived = (member: MemberListItem) => member.archived;

const newMemberTrialNotStarted = (member: MemberListItem) =>
    member.membership &&
    member.membership.trial !== "no-trial" &&
    !member.membership.endTrialDate;

const trialLapsed = (member: MemberListItem, now: Date) =>
    member.membership &&
    member.membership.trial !== "no-trial" &&
    !isTrialActive(member.membership, now);

const activeTrial = (member: MemberListItem, now: Date) =>
    member.membership &&
    member.membership.trial !== "no-trial" &&
    isTrialActive(member.membership, now);

const activePaying = (member: MemberListItem, now: Date) =>
    member.membership &&
    member.membership.trial === "no-trial" &&
    isMembershipActive(member.membership, now);

const isAssignedTo = (member: MemberListItem, currentAgent: string) =>
    member.assignedTo === currentAgent;

export function filterMembers(
    memberGroup: MemberGroupKey | undefined,
    currentAgent: string,
    members: MemberListItem[],
    now: Date,
) {
    switch (memberGroup) {
        case "assigned-to-me":
            return members.filter(
                (m) =>
                    isAssignedTo(m, currentAgent) &&
                    !archived(m) &&
                    !trialLapsed(m, now) &&
                    (newMemberTrialNotStarted(m) ||
                        activeTrial(m, now) ||
                        activePaying(m, now)),
            );
        case "lapsed-and-churned":
            return members.filter(
                (m) =>
                    !archived(m) &&
                    !newMemberTrialNotStarted(m) &&
                    !activeTrial(m, now) &&
                    (trialLapsed(m, now) || !activePaying(m, now)),
            );
        case "archived":
            return members.filter(archived);
        case "default":
        default:
            return members.filter(
                (m) =>
                    !archived(m) &&
                    !trialLapsed(m, now) &&
                    (newMemberTrialNotStarted(m) ||
                        activeTrial(m, now) ||
                        activePaying(m, now)),
            );
    }
}

export function filterMembersByAssignees(
    assignees: string[],
    members: MemberListItem[],
) {
    return takeIfStringMatches<MemberListItem>(
        members,
        (member) => member.assignedTo ?? unassignedAgent.id,
        assignees,
    );
}

export function filterMembersByGroups(groups: GroupId[], members: MemberListItem[]) {
    return takeIfAnyStringMatches<MemberListItem>(
        members,
        (member) => member.groups ?? [defaultGroup.id],
        groups,
    );
}

export function calculateEngagement(
    now: Date,
    lastMessageAt: FirebaseTimestamp | null | undefined,
): { level: EngagementLevel; label: string } {
    return calculateEngagementFromDate(
        now,
        lastMessageAt ? fromTimestampToDate(lastMessageAt) : null,
    );
}

export function calculateEngagementFromDate(
    now: Date,
    lastMessageAt: Date | null | undefined,
): { level: EngagementLevel; label: string } {
    if (!lastMessageAt) {
        return { level: "poor", label: EngagementLevelMap.poor };
    }

    const good = subDays(now, 7);
    const average = subDays(now, 14);

    if (isBefore(lastMessageAt, average)) {
        return { level: "poor", label: EngagementLevelMap.poor };
    }

    if (isBefore(lastMessageAt, good)) {
        return { level: "average", label: EngagementLevelMap.average };
    }

    return { level: "good", label: EngagementLevelMap.good };
}

export function filterMembersByEngagement(
    engagementLevels: EngagementLevel[],
    members: MemberListItem[],
    conversations: Dictionary<Conversation>,
    now: Date,
) {
    return takeIfStringMatches<MemberListItem>(
        members,
        (member) =>
            calculateEngagement(
                now,
                conversations[member.conversationId]
                    ? conversations[member.conversationId].lastMessageByMemberAt
                    : null,
            ).level,
        engagementLevels,
    );
}

export function filterMembersByMembershipTier(
    membershipTypes: MembershipTierId[],
    members: MemberListItem[],
) {
    return takeIfStringMatches<MemberListItem>(
        members,
        (member) => member.membership?.tier ?? noTierGroup.id,
        membershipTypes,
    );
}

export function filterMembersByMembershipStage(
    membershipStages: MembershipStageId[],
    members: MemberListItem[],
) {
    const nonMatchingString = "do-not-match";
    const transformTrialToStage = (trialId: TrialId): MembershipStageId =>
        isNoTrial(trialId) ? "paid" : "trial";

    return takeIfStringMatches<MemberListItem>(
        members,
        (member) => {
            if (member.membership?.trial) {
                return isTypeOf(member.membership.trial, TrialId)
                    ? transformTrialToStage(member.membership.trial)
                    : nonMatchingString;
            }
            return nonMatchingString;
        },
        membershipStages,
    );
}

export function memberAssignees(members: MemberListItem[], agents: Dictionary<Agent>) {
    // Map agent id to assigned member count
    const result: Dictionary<number> = {};

    members.forEach((member) => {
        const assignedTo = member.assignedTo ?? unassignedAgent.id;
        if (result[assignedTo]) {
            result[assignedTo] += 1;
        } else {
            result[assignedTo] = 1;
        }
    });

    return [unassignedAgent.id, ...Object.keys(agents)]
        .map((agentId) => ({
            assignee:
                agentId === unassignedAgent.id ? unassignedAgent : agents[agentId],
            count: result[agentId] ?? 0,
        }))
        .filter((it) => it.count > 0);
}

export function getMembershipTypeCounts(
    members: MemberListItem[],
    tiers: MembershipTier[],
): MembershipTypeCount[] {
    const tierCounts: MembershipTypeCount[] = [
        noTierGroup,
        ...tiers.filter((tier) => tier.active),
    ].map((tier) => ({
        id: tier.id,
        name: tier.name,
        active: tier.active,
        default: tier.default,
        count: 0,
    }));

    const countOneForTier = (tierId: string): { success: boolean } => {
        const tierIndex = tierCounts.findIndex((it) => it.id === tierId);

        if (tierIndex >= 0) {
            const { count, ...rest } = tierCounts[tierIndex];
            tierCounts[tierIndex] = {
                ...rest,
                count: count + 1,
            };
            return { success: true };
        }

        return { success: false };
    };

    members.forEach((member) => {
        if (member.membership?.tier) {
            const { success } = countOneForTier(member.membership.tier);

            if (!success) {
                countOneForTier(noTierGroup.id);
            }
        } else {
            countOneForTier(noTierGroup.id);
        }
    });

    return tierCounts;
}

export function getMembershipStageCounts(
    members: MemberListItem[],
): MembershipStageCount[] {
    const stageCounts: MembershipStageCount[] = MembershipStageId.options.map((id) => ({
        id,
        name: MembershipStageCountIdMap[id],
        count: 0,
    }));

    const countOneForStage = (trialId: MembershipStageId) => {
        const trialIndex = stageCounts.findIndex((it) => it.id === trialId);

        if (trialIndex >= 0) {
            const { count, ...rest } = stageCounts[trialIndex];
            stageCounts[trialIndex] = {
                ...rest,
                count: count + 1,
            };
        }
    };

    members.forEach((member) => {
        // TODO: Should we ignore if the tier is inactive?
        if (member.membership?.tier && isTypeOf(member.membership.trial, TrialId)) {
            if (!isNoTrial(member.membership.trial)) {
                countOneForStage("trial");
            } else {
                countOneForStage("paid");
            }
        }
    });

    return stageCounts;
}

export function getEngagementCounts(
    members: MemberListItem[],
    conversations: Dictionary<Conversation>,
    now: Date,
): FilterByEngagementLevel[] {
    const possibleEngagementLevels: FilterByEngagementLevel[] = EngagementLevelList.map(
        (level) => ({
            id: level,
            name: EngagementLevelMap[level],
            count: 0,
        }),
    );

    members.forEach((member) => {
        const { level } = calculateEngagement(
            now,
            conversations[member.conversationId]
                ? conversations[member.conversationId].lastMessageByMemberAt
                : null,
        );

        const matchIndex = possibleEngagementLevels.findIndex((it) => it.id === level);

        if (matchIndex >= 0) {
            const { count, ...rest } = possibleEngagementLevels[matchIndex];
            possibleEngagementLevels[matchIndex] = {
                ...rest,
                count: count + 1,
            };
        }
    });

    return possibleEngagementLevels;
}

export function sortMembers(
    members: MemberListItem[],
    conversations: Dictionary<Conversation>,
    settings: SortSettings,
) {
    switch (settings.by) {
        case "name": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                sortByStringComparator<MemberListItem>((m) =>
                    m ? memberNameBySurname(m) : "",
                )(a, b);
            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        case "creationDate": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                Lib.Utils.Sorting.sortByDateComparator<MemberListItem>((m) =>
                    m ? parseISO(m.createdAt) : undefined,
                )(a, b);
            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        case "lastSeenOnline": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                Lib.Utils.Sorting.sortByDateComparator<MemberListItem>((m) =>
                    m?.lastSeenAt ? parseISO(m.lastSeenAt) : undefined,
                )(a, b);
            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        case "activeRequests": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                sortByNumberComparator<MemberListItem>((m) =>
                    m ? m.activeRequests : 0,
                )(a, b);
            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        case "messageByMember": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                Lib.Utils.Sorting.sortByDateComparator<MemberListItem>((m) => {
                    if (!m) return undefined;
                    const conversation = conversations[m.conversationId];
                    if (!conversation) return undefined;
                    const { lastMessageByMemberAt } = conversation;
                    return lastMessageByMemberAt
                        ? fromTimestampToDate(lastMessageByMemberAt)
                        : undefined;
                }, true)(a, b);
            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        case "messageByAgent": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                Lib.Utils.Sorting.sortByDateComparator<MemberListItem>((m) => {
                    if (!m) return undefined;
                    const conversation = conversations[m.conversationId];
                    if (!conversation) return undefined;
                    const { lastMessageByAgentAt } = conversation;
                    return lastMessageByAgentAt
                        ? fromTimestampToDate(lastMessageByAgentAt)
                        : undefined;
                }, true)(a, b);
            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        case "membershipTrialExpiry": {
            const comparator = (a: MemberListItem, b: MemberListItem) =>
                Lib.Utils.Sorting.sortByDateComparator<MemberListItem>(
                    (m) =>
                        m?.membership && isTypeOf(m.membership, MembershipStageTrial)
                            ? m.membership.endTrialDate
                                ? new Date(m.membership.endTrialDate)
                                : undefined
                            : undefined,
                    true,
                )(a, b);

            return settings.order === "asc"
                ? [...members].sort(comparator)
                : [...members].sort(comparator).reverse();
        }

        default:
            return members;
    }
}

export type MembershipTileState = {
    title: string;
    subtitle: string;
    expired: boolean;
    trial: boolean;
    trialExtended: boolean;
};

function isMoreThan30Days(fromDate: Date, toDate: Date) {
    return Math.abs(differenceInCalendarDays(toDate, fromDate)) > 30;
}

function getExpiresState(membership: Membership, now: Date): string {
    const endDate = getEndDate(membership);
    if (!endDate) return "";

    if (isMoreThan30Days(now, new Date(endDate))) {
        return `Expires on ${formatDefaultDate(new Date(endDate), now)}`;
    }
    return `Expires in ${showDayMonthYearComparison(now, new Date(endDate)) ?? ""}`;
}

function isRevolutMembershipTier(membership: Membership): boolean {
    return (
        membership.tier === "revolut-limited" || membership.tier === "revolut-unlimited"
    );
}

export function createMembershipTileState(
    membership: Membership | undefined | null,
    tiers: MembershipTier[],
    now: Date,
): MembershipTileState {
    if (!membership) {
        return {
            title: "Not set",
            subtitle: "No active membership",
            expired: false,
            trial: false,
            trialExtended: false,
        };
    }

    const tier = tiers.find((t) => t.id === membership.tier);
    const title = tier?.name ?? `Unknown tier ${membership.tier}`;

    if (membership.trial === "no-trial") {
        const { endDate } = membership;
        const endDateObject = new Date(endDate);
        const isActive = isMembershipActive(membership, now);
        const isCancelled = isMembershipCancelled(membership);

        if (!isCancelled) {
            const subtitle = isActive
                ? `${showDayMonthYearComparison(now, endDateObject)} to renewal`
                : `Expired ${showDayMonthYearComparison(now, endDateObject)}`;
            return {
                title,
                subtitle,
                expired: !isActive,
                trial: false,
                trialExtended: false,
            };
        }

        const subtitle = isActive
            ? getExpiresState(membership, now)
            : `Expired on ${formatDefaultDate(endDateObject, now)}`;

        const cancelledPrefix = isRevolutMembershipTier(membership)
            ? ""
            : "Cancelled · ";

        return {
            title,
            subtitle: `${cancelledPrefix}${subtitle}`,
            expired: !isActive,
            trial: false,
            trialExtended: false,
        };
    }

    const { endTrialDate } = membership;

    if (!endTrialDate) {
        return {
            title,
            subtitle: "Not activated yet",
            expired: false,
            trial: true,
            trialExtended: false,
        };
    }

    const endTrialDateObject = new Date(endTrialDate);
    const expired = !isTrialActive(membership, now);
    const isExtended = isTrialExtended(membership);
    const extendedLabel = isExtended ? "Extended · " : "";

    if (expired) {
        return {
            title,
            subtitle: `${extendedLabel}Trial lapsed on ${formatDefaultDate(
                endTrialDateObject,
                now,
            )}`,
            expired,
            trial: true,
            trialExtended: isExtended,
        };
    }

    return {
        title,
        subtitle: `${extendedLabel}Expires in ${showDayMonthYearComparison(
            now,
            endTrialDateObject,
        )}`,
        expired,
        trial: true,
        trialExtended: isExtended,
    };
}
